import { BlockerComponentProps, BlockerData, BlockerType } from '@cash-web/shared-flow-data-access-context'

import { BlockerAddress } from './BlockerAddress/BlockerAddress'
import { BlockerAlias } from './BlockerAlias/BlockerAlias'
import { BlockerAmount } from './BlockerAmount/BlockerAmount'
import { BlockerAuthorizeDigitalWalletPayment } from './BlockerAuthorizeDigitalWalletPayment/BlockerAuthorizeDigitalWalletPayment'
import { BlockerCard } from './BlockerCard/BlockerCard'
import { BlockerCardCustomization } from './BlockerCardCustomization/BlockerCardCustomization'
import { BlockerCashtag } from './BlockerCashtag/BlockerCashtag'
import { BlockerConfirm } from './BlockerConfirm/BlockerConfirm'
import { BlockerDisclosure } from './BlockerDisclosure/BlockerDisclosure'
import { BlockerEmail } from './BlockerEmail/BlockerEmail'
import { BlockerEmailVerification } from './BlockerEmailVerification/BlockerEmailVerification'
import { BlockerIdentityVerification } from './BlockerIdentityVerification/BlockerIdentityVerification'
import { BlockerInstrumentSelection } from './BlockerInstrumentSelection/BlockerInstrumentSelection'
import { BlockerInstrumentVerification } from './BlockerInstrumentVerification/BlockerInstrumentVerification'
import { BlockerInvestmentEntitySelection } from './BlockerInvestmentEntitySelection/BlockerInvestmentEntitySelection'
import { BlockerMultiAllocation } from './BlockerMultiAllocation/BlockerMultiAllocation'
import { BlockerName } from './BlockerName/BlockerName'
import { BlockerOnboardingInternalRoute } from './BlockerOnboardingInternalRoute/BlockerOnboardingInternalRoute'
import { BlockerPasscodeCreation } from './BlockerPasscodeCreation/BlockerPasscodeCreation'
import { BlockerPasscodeVerification } from './BlockerPasscodeVerification/BlockerPasscodeVerification'
import { BlockerPasswordCreation } from './BlockerPasswordCreation/BlockerPasswordCreation'
import { BlockerPasswordVerification } from './BlockerPasswordVerification/BlockerPasswordVerification'
import { BlockerPayWithCashAuthorization } from './BlockerPayWithCashAuthorization/BlockerPayWithCashAuthorization'
import { BlockerPhoneNumber } from './BlockerPhoneNumber/BlockerPhoneNumber'
import { BlockerPhoneVerification } from './BlockerPhoneVerification/BlockerPhoneVerification'
import { BlockerRatePlan } from './BlockerRatePlan/BlockerRatePlan'
import { BlockerRegion } from './BlockerRegion/BlockerRegion'
import { BlockerRewardCode } from './BlockerRewardCode/BlockerRewardCode'
import { BlockerSelection } from './BlockerSelection/BlockerSelection'
import { BlockerSelectSponsor } from './BlockerSelectSponsor/BlockerSelectSponsor'
import { BlockerSetAllocationAmount } from './BlockerSetAllocationAmount/BlockerSetAllocationAmount'
import { BlockerSignature } from './BlockerSignature/BlockerSignature'
import { BlockerStartTotpEnrollment } from './BlockerStartTotpEnrollment/BlockerStartTotpEnrollment'
import { BlockerStatusInterstitial } from './BlockerStatusInterstitial/BlockerStatusInterstitial'
import { BlockerSupportRequired } from './BlockerSupportRequired/BlockerSupportRequired'
import { BlockerTaxWebViewBlocker } from './BlockerTaxWebViewBlocker/BlockerTaxWebViewBlocker'
import { BlockerTotpRecoveryCodes } from './BlockerTotpRecoveryCodes/BlockerTotpRecoveryCodes'
import { FormBlocker } from './FormBlocker/FormBlocker'
import { RoundUpOnboardingBlocker } from './RoundUpOnboardingBlocker/RoundUpOnboardingBlocker'

export type RegisteredBlockerComponents = {
  [key in BlockerType]?: React.FC<BlockerComponentProps<key>>
}
// this is a pattern that we should be deprecate, we shouldn't use this unless absolutely necessary
export type RegisteredCustomBlockerComponents = { [key: string]: React.FC<BlockerComponentProps<'form'>> }

export type RegistryLookupEntry<Type extends BlockerType> = {
  id?: string
  type: Type
  blocker: BlockerData<Type>
}
export type RegistryLookup = {
  [Type in BlockerType]: RegistryLookupEntry<Type>
}[BlockerType]

// this function exists to avoid circular dependencies by lazy referencing the components
function lookupBlocker(name: BlockerType) {
  /* eslint sort-keys: "warn" */
  const blockerComponentRegistry: RegisteredBlockerComponents = {
    address: BlockerAddress,
    alias_blocker: BlockerAlias,
    amount_blocker: BlockerAmount,
    authorize_digital_wallet_payment_blocker: BlockerAuthorizeDigitalWalletPayment,
    card: BlockerCard,
    card_customization: BlockerCardCustomization,
    cashtag: BlockerCashtag,
    confirm: BlockerConfirm,
    disclosure: BlockerDisclosure,
    email: BlockerEmail,
    email_verification: BlockerEmailVerification,
    form: FormBlocker,
    identity_verification: BlockerIdentityVerification,
    instrument_selection_blocker: BlockerInstrumentSelection,
    instrument_verification: BlockerInstrumentVerification,
    investment_entity_selection_blocker: BlockerInvestmentEntitySelection,
    multi_allocation_blocker: BlockerMultiAllocation,
    name: BlockerName,
    onboarding_internal_route_blocker: BlockerOnboardingInternalRoute,
    passcode_creation: BlockerPasscodeCreation,
    passcode_verification: BlockerPasscodeVerification,
    password_creation_blocker: BlockerPasswordCreation,
    password_verification_blocker: BlockerPasswordVerification,
    pay_with_cash_authorization_blocker: BlockerPayWithCashAuthorization,
    phone_number: BlockerPhoneNumber,
    phone_verification: BlockerPhoneVerification,
    rate_plan: BlockerRatePlan,
    region: BlockerRegion,
    reward_code: BlockerRewardCode,
    round_up_onboarding_blocker: RoundUpOnboardingBlocker,
    select_sponsors_blocker: BlockerSelectSponsor,
    selection: BlockerSelection,
    set_paycheck_allocation_amount_blocker: BlockerSetAllocationAmount,
    signature: BlockerSignature,
    status_interstitial_blocker: BlockerStatusInterstitial,
    support_required: BlockerSupportRequired,
    tax_web_view_blocker: BlockerTaxWebViewBlocker,
  }
  /* eslint sort-keys: "off" */

  return blockerComponentRegistry[name]
}

/* eslint sort-keys: "warn" */
const customFormBlockerRegistry: RegisteredCustomBlockerComponents = {
  /**
   * custom components that should technically be rendered by the form blocker.
   * this is achieved by using the id of the form blocker instead of by type
   * we shouldn't do this unless absolutely necessary
   */
  acknowledge_recovery_codes: BlockerTotpRecoveryCodes,
  start_totp_enrollment: BlockerStartTotpEnrollment,
}
/* eslint-disable sort-keys */

export const getRegisteredComponent = (lookup: RegistryLookup) => {
  // look up by id (form blocker) first
  if (lookup.id != null && customFormBlockerRegistry[lookup.id] != null) {
    return customFormBlockerRegistry[lookup.id]
  }
  // look up by type (default)
  if (lookup.type != null && lookupBlocker(lookup.type) != null) {
    return lookupBlocker(lookup.type)
  }
  // if we didn't find anything return null
  return null
}
